import { BellOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Col, Descriptions, Drawer, Form, Image, Input, Modal, Result, Row, Select, Segmented, Tag, Tooltip, Typography, message } from 'antd';
import axios from '../config/axios';
import dayjs from '../config/dayjs';
import { User, UserLocation } from '../types/user.types';
import { handleAPIError } from '../utils/helpers';
import { Spinner } from './spinner';
import CopyableText from './copyable-text';
import { useContext, useState } from 'react';
import { AuthContext } from '../context/auth.context';
import { UserLocationsMap } from './user-locations-map';
import { UserContacts } from './user-contacts';
import { UserVerificationCall } from './user-verification-call';
import { UserCreditScore } from './user-credit-score';
import { UserCreditReport } from './user-credit-report';

const { Title } = Typography;

interface UserDetailsProps {
	id: number;
	onClose: () => void;
}

export const UserDetails = ({ id, onClose }: UserDetailsProps) => {
	const queryClient = useQueryClient();
	const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
	const [isNotifModalVisible, setIsNotifModalVisible] = useState(false);
	const [activeSegment, setActiveSegment] = useState<string>('user-details');
	const [form] = Form.useForm();
	const [notifForm] = Form.useForm();
	const { user: adminUser } = useContext(AuthContext);
	const [dupUserId, setDupUserId] = useState<number | null>(null);

	// Fetch user data
	const { data, isLoading, error } = useQuery({
		queryKey: ['user', id],
		queryFn: async () => {
			const response = await axios.get(`/admin/users/${id}`, { withCredentials: true });
			return response.data;
		},
		enabled: !!id,
	});

	// Update user status mutation
	const { mutate: updateStatus, isPending } = useMutation({
		mutationFn: async (status: string) => {
			const response = await axios.patch(`/admin/users/${id}`, { status }, { withCredentials: true });
			return response.data;
		},
		onSuccess: () => {
			message.success('User status updated successfully');
			setIsStatusModalVisible(false);
			// Refresh queries
			queryClient.invalidateQueries({ queryKey: ['user', id] });
			queryClient.invalidateQueries({ queryKey: ['users'] });
		},
		onError: (error) => {
			handleAPIError(error);
		},
	});

	// Handle form submission
	const handleStatusUpdate = (values: { status: string }) => {
		updateStatus(values.status);
	};

	// Send push notification mutation
	const { mutate: sendNotification, isPending: isNotifPending } = useMutation({
		mutationFn: async (values: { title: string; body: string; type: string }) => {
			const response = await axios.post(`/admin/users/${id}/push-notification`, values, { withCredentials: true });
			return response.data;
		},
		onSuccess: () => {
			message.success('Notification sent successfully');
			setIsNotifModalVisible(false);
			notifForm.resetFields();
		},
		onError: (error) => {
			handleAPIError(error);
		},
	});

	// Show edit modal
	const showStatusModal = () => {
		form.setFieldsValue({ status: user?.status });
		setIsStatusModalVisible(true);
	};

	if (error) {
		handleAPIError(error);
		return (
			<Card className="shadow-sm">
				<Result
					status="error"
					title="Failed to load user data"
					subTitle="Please try again or contact support if the problem persists."
				/>
			</Card>
		);
	}

	const user: User = data?.results.user;

	// Duplicate NOK check — runs once user is loaded and has a next_of_kin
	const { data: dupNokUsers = [] } = useQuery<{ id: number; first_name: string; last_name: string; phone_number: string }[]>({
		queryKey: ['duplicate-nok', user?.next_of_kin, id],
		queryFn: async () => {
			const res = await axios.get('/admin/users/duplicate-nok', {
				params: { nok: user.next_of_kin, exclude_user: id },
				withCredentials: true,
			});
			return res.data.results;
		},
		enabled: !!user?.next_of_kin,
	});

	const getStatusColor = (status: User['status']) => {
		switch (status) {
			case 'verified':
				return 'green';
			case 'pending verification':
				return 'blue';
			case 'incomplete':
				return 'gold';
			case 'blocked':
				return 'red';
			case 'temporary blocked':
				return 'volcano';
			default:
				return 'default';
		}
	};

	const statusOptions = [
		{ value: 'verified', label: 'Verified' },
		{ value: 'pending verification', label: 'Pending Verification' },
		{ value: 'incomplete', label: 'Incomplete' },
		{ value: 'blocked', label: 'Blocked' },
		{ value: 'temporary blocked', label: 'Temporary Blocked' },
	];

	const userLocations: UserLocation[] = data?.results.locations || [];

	return (
		<>
			<Drawer
				open={true}
				onClose={onClose}
				title="User Details"
				width={'80%'}
			>
				{isLoading ? (
					<div className="flex h-[50dvh] w-full items-center justify-center">
						<Spinner />
					</div>
				) : (
					<div className="space-y-6">
						{/* Header with profile picture and close button */}
						<Card
							className="shadow-sm"
							extra={
								<Button
									icon={<CloseOutlined />}
									onClick={onClose}
									type="text"
								/>
							}
						>
							<Row
								gutter={[24, 24]}
								align="middle"
							>
								<Col
									xs={24}
									md={6}
									className="text-center"
								>
									<Image
										src={user.dp ?? '/images/avatar-placeholder.png'}
										alt={`${user.first_name} ${user.last_name}`}
										width={120}
										height={120}
										className="rounded-full object-cover"
										fallback="/images/avatar-placeholder.png"
									/>
								</Col>
								<Col
									xs={24}
									md={18}
								>
									<Title
										level={3}
										className="m-0"
									>{`${user.first_name} ${user.last_name}`}</Title>
									<CopyableText text={user.phone_number}>
										<a
											className="text-lg"
											href={`tel:+${user.phone_number}`}
										>
											{user.phone_number}
										</a>
									</CopyableText>
									<div className="mt-2 flex items-center">
										<Tag
											color={getStatusColor(user.status)}
											className="flex items-center"
										>
											{user.status.toUpperCase()}
											{(adminUser?.admin_type === 'super_admin' || adminUser?.admin_type === 'support_admin') && (
												<Button
													type="text"
													icon={<EditOutlined />}
													onClick={showStatusModal}
													className="ml-1 flex items-center justify-center"
													size="small"
												/>
											)}
										</Tag>

										<Tag color={user.role === 'admin' ? 'purple' : 'cyan'}>{user.role.toUpperCase()}</Tag>
										{user.is_super_admin && <Tag color="magenta">SUPER ADMIN</Tag>}
										<Button
										size="small"
										icon={<BellOutlined />}
										onClick={() => setIsNotifModalVisible(true)}
									>
										Notify
									</Button>
									</div>
								</Col>
							</Row>
						</Card>

						{/* Segmented Control */}
						<div className="flex justify-start">
							<Segmented
								options={[
									{ label: 'User Details', value: 'user-details' },
									{ label: 'Contacts', value: 'contacts' },
									{ label: 'Verification Call', value: 'verification-call' },
									{ label: 'Credit Score', value: 'credit-score' },
									...(adminUser?.admin_type === 'super_admin' || adminUser?.admin_type === 'support_admin'
										? [{ label: 'Credit Report', value: 'credit-report' }]
										: []),
								]}
								value={activeSegment}
								onChange={(value) => setActiveSegment(value as string)}
							/>
						</div>

						{/* User Details Segment */}
						{activeSegment === 'user-details' && (
							<div className="space-y-6">
								{/* Personal Information */}
								<Card
									title="Personal Information"
									className="shadow-sm"
								>
									<Descriptions
										bordered
										column={{ xs: 1, sm: 2, md: 3 }}
										size="small"
									>
										<Descriptions.Item label="First Name">{user.first_name}</Descriptions.Item>
										<Descriptions.Item label="Last Name">{user.last_name}</Descriptions.Item>
										<Descriptions.Item label="Email">{user.email || 'N/A'}</Descriptions.Item>
										<Descriptions.Item label="Phone">{user.phone_number}</Descriptions.Item>
										<Descriptions.Item label="Gender">{user.sex || 'N/A'}</Descriptions.Item>
										<Descriptions.Item label="Date of Birth">
											{user.date_of_birth ? dayjs(user.date_of_birth).format('DD-MM-YYYY') : 'N/A'}
										</Descriptions.Item>
										<Descriptions.Item label="Next of Kin">
										<div className="flex flex-col gap-1">
											<span>{user.next_of_kin || 'N/A'}</span>
											{dupNokUsers.length > 0 && (
												<div className="mt-1 flex flex-col gap-1">
													<Tag color="red" className="m-0 w-fit">
														⚠ {dupNokUsers.length} other account{dupNokUsers.length > 1 ? 's' : ''} use this NOK
													</Tag>
													{dupNokUsers.map((u) => (
														<Tag
															key={u.id}
															color="orange"
															className="m-0 w-fit cursor-pointer"
															onClick={() => setDupUserId(u.id)}
														>
															{u.first_name} {u.last_name} · {u.phone_number}
														</Tag>
													))}
												</div>
											)}
										</div>
									</Descriptions.Item>
										<Descriptions.Item label="NIN">
											<CopyableText text={user.nin} />
										</Descriptions.Item>
									</Descriptions>
								</Card>

								{/* Location Information */}
								<Card
									title="Location Information"
									className="shadow-sm"
								>
									<Descriptions
										bordered
										column={{ xs: 1, sm: 2 }}
										size="small"
									>
										<Descriptions.Item label="District">{user.district || 'N/A'}</Descriptions.Item>
										<Descriptions.Item label="Town">{user.town || 'N/A'}</Descriptions.Item>
									</Descriptions>
								</Card>

								{/* User Locations Map */}
								<UserLocationsMap userLocations={userLocations} />

								{/* ID Documents */}
								<Card
									title="Identification Documents"
									className="shadow-sm"
								>
									<Row gutter={[16, 16]}>
										<Col
											xs={24}
											md={12}
										>
											<div className="rounded border p-2">
												<div className="mb-2 font-medium">National ID (Front)</div>
												<Image
													src={user.nid_front ?? '/images/id-placeholder.png'}
													alt="National ID Front"
													width="100%"
													height={400}
													className="rounded-md object-cover"
													fallback="/images/id-placeholder.png"
												/>
											</div>
										</Col>
										<Col
											xs={24}
											md={12}
										>
											<div className="rounded border p-2">
												<div className="mb-2 font-medium">National ID (Back)</div>
												<Image
													src={user.nid_back ?? '/images/id-placeholder.png'}
													alt="National ID Back"
													width="100%"
													height={400}
													className="rounded-md object-cover"
													fallback="/images/id-placeholder.png"
												/>
											</div>
										</Col>
									</Row>
								</Card>

								{/* Account Information */}
								<Card
									title="Account Information"
									className="shadow-sm"
								>
									<Descriptions
										bordered
										column={{ xs: 1, sm: 2, md: 3 }}
										size="small"
									>
										<Descriptions.Item label="User ID">{user.id}</Descriptions.Item>
										<Descriptions.Item
											label="Role"
											className="uppercase"
										>
											{user.role}
										</Descriptions.Item>
										<Descriptions.Item
											label="Status"
											className="uppercase"
										>
											{user.status}
										</Descriptions.Item>
										<Descriptions.Item label="Active">{user.is_active ? 'Yes' : 'No'}</Descriptions.Item>
										<Descriptions.Item label="Staff Member">{user.is_staff ? 'Yes' : 'No'}</Descriptions.Item>
										<Descriptions.Item label="Super Admin">{user.is_super_admin ? 'Yes' : 'No'}</Descriptions.Item>
										<Descriptions.Item label="Created At">{dayjs(user.created_at).format('DD-MM-YYYY LT')}</Descriptions.Item>
										<Descriptions.Item label="Updated At">{dayjs(user.updated_at).format('DD-MM-YYYY LT')}</Descriptions.Item>
										<Descriptions.Item label="Last Login">
											{user.last_login ? dayjs(user.last_login).format('DD-MM-YYYY LT') : 'Never'}
										</Descriptions.Item>
										<Descriptions.Item label="Last Active">
											{user.last_active_at ? dayjs(user.last_active_at).format('DD-MM-YYYY LT') : 'Never'}
										</Descriptions.Item>
										<Descriptions.Item label="Device ID">{user.device_id || 'N/A'}</Descriptions.Item>
										<Descriptions.Item label="OneSignal Token">
											{user.os_notification_token ? (
												<Tooltip title={user.os_notification_token}>
													<span className="text-xs">{(user.os_notification_token as string).substring(0, 10)}...</span>
												</Tooltip>
											) : (
												<span className="text-xs">N/A</span>
											)}
										</Descriptions.Item>
										<Descriptions.Item label="FCM Token">
											{user.fcm_token ? (
												<CopyableText text={user.fcm_token}>
													<Tooltip title={user.fcm_token}>
														<span className="text-xs">{(user.fcm_token as string).substring(0, 20)}...</span>
													</Tooltip>
												</CopyableText>
											) : (
												<span className="text-xs text-red-400">Not registered</span>
											)}
										</Descriptions.Item>
									</Descriptions>
								</Card>
							</div>
						)}

						{/* Contacts Segment */}
						{activeSegment === 'contacts' && <UserContacts userId={id} />}

						{/* Verification Call Segment */}
						{activeSegment === 'verification-call' && (
							<UserVerificationCall
								userId={id}
								readOnly={user.status === 'verified' && adminUser?.admin_type !== 'super_admin'}
							/>
						)}

						{/* Credit Score Segment */}
						{activeSegment === 'credit-score' && (
							<UserCreditScore userId={id} userNin={user.nin} />
						)}

						{/* Credit Report Segment */}
						{activeSegment === 'credit-report' && (
							<UserCreditReport userId={id} userNin={user.nin} />
						)}
					</div>
				)}
			</Drawer>

			{/* Status Edit Modal */}
			<Modal
				title="Update User Status"
				open={isStatusModalVisible}
				onCancel={() => setIsStatusModalVisible(false)}
				footer={null}
				destroyOnClose
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleStatusUpdate}
					initialValues={{ status: user?.status }}
				>
					<Form.Item
						name="status"
						label="Status"
						rules={[{ required: true, message: 'Please select a status' }]}
					>
						<Select options={statusOptions} />
					</Form.Item>

					<Form.Item className="mb-0 text-right">
						<Button
							onClick={() => setIsStatusModalVisible(false)}
							className="mr-2"
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isPending}
						>
							Update Status
						</Button>
					</Form.Item>
				</Form>
			</Modal>
			{/* Push Notification Modal */}
			<Modal
				title="Send Push Notification"
				open={isNotifModalVisible}
				onCancel={() => {
					setIsNotifModalVisible(false);
					notifForm.resetFields();
				}}
				footer={null}
				destroyOnClose
			>
				<Form
					form={notifForm}
					layout="vertical"
					onFinish={(values) => sendNotification(values)}
					initialValues={{ type: 'account_update' }}
				>
					<Form.Item
						name="type"
						label="Notification Type"
					>
						<Select
							options={[
								{ value: 'account_update', label: 'Account Update' },
								{ value: 'payment_reminder', label: 'Payment Reminder' },
								{ value: 'payment_overdue', label: 'Payment Overdue' },
								{ value: 'payment_confirmed', label: 'Payment Confirmed' },
							]}
						/>
					</Form.Item>
					<Form.Item
						name="title"
						label="Title"
						rules={[{ required: true, message: 'Please enter a title' }]}
					>
						<Input placeholder="Notification title" />
					</Form.Item>
					<Form.Item
						name="body"
						label="Message"
						rules={[{ required: true, message: 'Please enter a message' }]}
					>
						<Input.TextArea rows={3} placeholder="Notification message" />
					</Form.Item>
					<Form.Item className="mb-0 text-right">
						<Button
							onClick={() => {
								setIsNotifModalVisible(false);
								notifForm.resetFields();
							}}
							className="mr-2"
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isNotifPending}
							icon={<BellOutlined />}
						>
							Send Notification
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			{/* Nested drawer for duplicate NOK user */}
			{dupUserId && (
				<UserDetails id={dupUserId} onClose={() => setDupUserId(null)} />
			)}
		</>
	);
};
